import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { of, Subject } from 'rxjs';
import { filter, map, switchMap, take, takeUntil } from 'rxjs/operators';
import { ClientService } from '../../clients/client.service';
import { DocumentService } from '../../documents/document.service';
import { OrganizationService } from '../../organizations/organization.service';
import { PepUcfirstPipe } from '../../shared/pipes/pep-ucfirst.pipe';
import { Client, Document, Organization } from '../../shared/sdk';
import { EmailService } from '../../shared/services/email.service';
import {
  Language,
  languages,
  LanguageService,
  locales,
} from '../../shared/services/language.service';

@Component({
  selector: 'app-email-document',
  templateUrl: './email-document.component.html',
  styleUrls: ['./email-document.component.css'],
})
export class EmailDocumentComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @Input() document: Document;
  activeOrg: Organization;
  attachmentLanguage: string;
  attachmentName: string;
  attachments: { id: string; value: boolean; name: string }[];
  currentLang: string;
  emailForm: FormGroup;
  languages: Array<Language> = [];
  loading: boolean;
  locales = locales;
  sent = false;
  typeName: string;
  private onDestroy$: Subject<void> = new Subject();
  @ViewChild('email') email: ElementRef;

  constructor(
    public activeModal: NgbActiveModal,
    public clientService: ClientService,
    public emailService: EmailService,
    public orgService: OrganizationService,
    public translate: TranslateService,
    private documentService: DocumentService,
    private fb: FormBuilder,
    private languageService: LanguageService,
  ) {}

  ngOnInit(): void {
    this.attachmentName = this.document.number || this.document.type;

    if (this.document.attachments) {
      this.attachments = this.document.attachments.map((el) => {
        return { id: el.id as string, value: true, name: el.nameOriginal };
      });
    } else {
      this.attachments = [];
    }
    Object.keys(languages).forEach((l) => this.languages.push(languages[l]));

    // Form control
    this.emailForm = this.fb.group({
      recipients: ['', Validators.required],
      subject: [''],
      message: ['', Validators.required],
      sendCopy: [false],
      associateEmail: [false], // Associate email to client
      eslogAttachment: [
        {
          value: this.document.isValidEslog,
          disabled: !this.document.isValidEslog,
        },
      ],
    });

    this.languageService.current
      .pipe(
        switchMap((lang) => {
          this.currentLang = lang;
          if (!this.attachmentLanguage) {
            this.attachmentLanguage = lang;
          }

          return this.documentService.documentTypes;
        }),
        take(1),
      )
      .subscribe((docTypeNames) => {
        this.typeName = docTypeNames[this.document.type].name;
        const typeNameUpper = new PepUcfirstPipe().transform(this.typeName);

        this.emailForm.controls.subject.setValue(
          `${typeNameUpper} ${this.document.number}`,
        );
      });

    this.orgService.active
      .pipe(
        filter((org) => !!org),
        switchMap((org: Organization) => {
          this.activeOrg = org;

          let localeIdx = locales.findIndex(
            (l) => l.symbol === this.activeOrg.locale.split('-')[0],
          );
          // Fallback to english
          localeIdx =
            localeIdx !== -1
              ? localeIdx
              : locales.findIndex((l) => l.symbol === 'en');
          const currentLocale = locales[localeIdx];

          this.locales = [
            currentLocale,
            ...locales.slice(0, localeIdx),
            ...locales.slice(localeIdx + 1),
          ];

          this.setMessage();

          if (this.document.clientId) {
            return this.clientService.get(org.id, {
              where: { id: this.document.clientId },
              deleted: false,
              limit: 1,
            });
          } else {
            return of({ body: [] });
          }
        }),
        map(({ body }) => body),
        takeUntil(this.onDestroy$),
      )
      .subscribe((clients: Client[]) => {
        let associate = false;

        if (clients[0] && clients[0].email) {
          this.emailForm.controls.recipients.setValue(clients[0].email);
        } else if (this.document._documentClient?.email) {
          this.emailForm.controls.recipients.setValue(
            this.document._documentClient.email,
          );
          associate = true;
        } else {
          associate = true;
        }

        if (associate) {
          this.emailForm.controls.associateEmail.setValue(true);
        }
      });
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.email.nativeElement.focus(), 100);
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  onChangeLanguage(locale: string): void {
    if (this.attachmentLanguage === locale) {
      return;
    }

    this.attachmentLanguage = locale;
    this.setMessage(locale);
  }

  onClose(): void {
    this.activeModal.dismiss();
  }

  onSend(): void {
    this.sent = true;

    if (this.emailForm.valid) {
      const excludedAttachmentIds = this.attachments
        .filter((el) => !el.value)
        .map((el) => el.id);

      const data: any = this.emailForm.value;

      const obs = this.emailService.sendDocument(
        this.document.id,
        data.recipients,
        data.message,
        data.subject,
        data.sendCopy,
        true,
        excludedAttachmentIds,
        data.eslogAttachment,
        this.attachmentLanguage,
      );

      // Must subscribe to run
      this.activeModal.close(obs);
    }
  }

  setMessage(locale?: string): void {
    const unitId = this.document.unitId;
    let defaultMessage = '';

    if (unitId) {
      const unitDef = this.orgService.findDefault(
        this.activeOrg,
        `unit_${unitId}_email_document_${this.document.type}`,
      );

      if (unitDef) {
        if (
          locale &&
          unitDef.l10n &&
          unitDef.l10n[`email_document_${locale}`]
        ) {
          defaultMessage = unitDef.l10n[`email_document_${locale}`];
        }

        if (!defaultMessage && unitDef.value) {
          defaultMessage = unitDef.value;
        }
      }
    }

    if (!defaultMessage) {
      const defMessage = this.orgService.findDefault(
        this.activeOrg,
        `email_document_${this.document.type}`,
      );

      if (defMessage) {
        if (
          locale &&
          defMessage.l10n &&
          defMessage.l10n[`email_document_${locale}`]
        ) {
          defaultMessage = defMessage.l10n[`email_document_${locale}`];
        }

        if (!defaultMessage && defMessage.value) {
          defaultMessage = defMessage.value;
        }
      }
    }

    this.emailForm.controls.message.setValue(defaultMessage);
  }
}
